import { UserType } from "./users/UserTypes";
import { ProductVMType } from "./products/ProductCommandsType";
import { MachineType } from "./MachineType";

export enum ProductionStatusEnum {
  CANCELLED = -1,
  DONE = 1,
  TODO = 0,
}

export enum ProductionTaskStateEnum {
  TODO = "TODO",
  ACTIVE = "ACTIVE",
  DONE = "DONE",
}

export enum ProductionTaskEnum {
  CAM = "cam",
  REVIEW = "review",
  MATERIALCUT = "production",
  MILLING = "mill",
  TURNING = "turning",
  QUALITYCONTROL = "afterwork",
  MAUNALFINISH = "manualfinish",
  SHIPPING = "shipping",
  OUTSOURCE = "outsource",
  ANODIZE = "anodize",
  ELECTROPLISH = "electroplish",
  BEADBLAST = "beadblast",
  DONE = "done",
}

export type CreateProductionType = {
  productId: number;
  customerId?: number;
  deadline?: string;
  orderId?: number;
  outsourced?: boolean;
};

export type UpdateProductionType = {
  customerId?: number;
  // No `deadline` here on purpose. Moving a Production Deadline needs
  // a reason for the change, which this endpoint has nowhere to carry - the
  // backend rejects the field. Use apiChangeProductionDeadline instead.
  orderId?: number;
  priority?: number;
  outsourced?: boolean;
  status?: ProductionStatusEnum;
  tasks?: UpdateProductionTaskType[];
};

export type CreateProductionTaskType = {
  name: string;
  productionId: number;
  description?: string;
  priority?: number;
  deadline?: string;
  leadTime?: number;
  workTime?: number;
  progressMax?: number;
  users?: number[];
  machines?: number[];
  state?: ProductionTaskStateEnum;
  allowUsers?: boolean;
  allowMachines?: boolean;
  allowInquiries?: boolean;
  allowWorkTime?: boolean;
  allowLeadTime?: boolean;
  allowProgress?: boolean;
};

// EE-216: one delay reason, as GET /v1/ecommerce/productions/delay-reasons
// serves it. The same list backs the QC delay, the order completion delay and
// the Production Deadline change. It is a setup table backend-side, edited in
// the database, so never hardcode it here.
export type DelayReasonOptionType = {
  code: string;
  label: string;
  requiresComment: boolean;
  /**
   * Inactive reasons are still served, so a record filed under a reason
   * that was later retired keeps its label. They must not be offered as a
   * choice - the dialogs filter on this.
   */
  isActive: boolean;
};

// One recorded move of a Production Deadline, as
// GET /v1/ecommerce/productions/<id>/deadline-changes serves it. The
// production itself carries only the most recent move; this is the trail.
export type ProductionDeadlineChangeType = {
  id: number;
  productionId: number;
  /** Null for the first move of a production that had no deadline yet. */
  previousDate: string | null;
  newDate: string | null;
  reasons: string[];
  comment: string | null;
  changedById: number | null;
  changedAt: string | null;
};

export type ChangeProductionDeadlineType = {
  productionIds: number[];
  /** YYYY-MM-DD, or a full ISO timestamp. Stored as the day. */
  deadline: string;
  reasons: string[];
  comment?: string;
};

export type UpdateProductionTaskType = {
  id: number;
  name?: string;
  description?: string;
  priority?: number;
  deadline?: string;
  leadTime?: number;
  workTime?: number;
  progress?: number;
  progressMax?: number;
  users?: number[];
  machines?: number[];
  inquiries?: number[];
  requisitions?: number[];
  // EE-253: the requisition lines the task holds on. Sent whole; when it is
  // left out the backend keeps the lines of the requisitions that stay.
  requisitionItems?: number[];
  state?: ProductionTaskStateEnum;
  // EE-216: sent only when closing the last QC step of a production that is
  // past its deadline. Rides on the same request as the state change so the
  // step can never be closed without its reason.
  delayPrimaryReason?: string;
  delayContributingReasons?: string[];
  delayComment?: string;
};

// TODO: remove
export type ProductionTaskDetailedType = {
  id: number;
  created: string;
  modified: string;
  name: string;
  priority: number;
  productionId: number;
  users: UserType[];
  machines: MachineType[];
  state: ProductionTaskStateEnum;
  product: ProductVMType;
  customer: UserType;
  orderId: number;
};

export type ProductionTaskType = {
  id: number;
  created: string;
  modified: string;
  created_by: string;
  modified_by: string;
  created_by_id: number;
  modified_by_id: number;
  productionId: number;
  priority: number;
  name: string;
  description: string;
  state: ProductionTaskStateEnum;
  progress: number;
  progressMax: number;
  workTime: number;
  leadTime: number;
  deadline: string;
  allowUsers: boolean;
  allowMachines: boolean;
  allowInquiries: boolean;
  allowWorkTime: boolean;
  allowLeadTime: boolean;
  allowProgress: boolean;
  prevTaskId: number;
  nextTaskId: number;
  users: {
    id: number;
    firstName: string;
    lastName: string;
  }[];
  machines: {
    id: number;
    name: string;
    label: string;
  }[];
  // Frozen: the picker writes `requisitions` now. These stay so existing
  // links keep rendering and can still be removed.
  inquiries: {
    id: number;
    name: string;
    label: string;
  }[];
  requisitions: TaskRequisitionType[];
  // EE-253: the requisition lines this task holds on - blocking follows
  // these, line by line, not the requisition header.
  requisitionItems?: number[];
  // Set when material this task holds on went missing again after the task
  // had started. A running task is not re-blocked; it gets this badge.
  purchasingWarning?: boolean;
};

export type TaskRequisitionType = {
  id: number;
  requisitionNo: string | null;
  status: string | null;
  typeCode: string | null;
  typeLabel: string | null;
};

export type ProductionVMType = {
  id: number;
  created: string;
  modified: string;
  created_by: string;
  modified_by: string;
  created_by_id: number;
  modified_by_id: number;
  productId: number;
  product: ProductVMType;
  orderId: number;
  customerId: number;
  deadlineDate: string;
  priority: number;
  status: ProductionStatusEnum;
  outsourced: boolean;
  tasks: ProductionTaskType[];
  type: string;
  orderDate: string;
  orderCompanyName: string;
  orderMessage: string;
  blockedBy: number[];
  // EE-216: null on every production that met its deadline.
  delayPrimaryReason?: string | null;
  delayContributingReasons?: string[];
  delayComment?: string | null;
  delayRecordedAt?: string | null;
  // The manual override behind deadlineDate. Empty/null while the
  // deadline is still the one the system calculated.
  deadlineChangeReasons?: string[];
  deadlineChangeComment?: string | null;
  deadlineChangedAt?: string | null;
  deadlineChangedById?: number | null;
  deadlineOriginalDate?: string | null;
};

export type SimpleProductionVMType = {
  id: number;
  created: string;
  modified: string;
  created_by_id: number;
  modified_by_id: number;
  productId: number;
  orderId: number;
  customerId: number;
  deadlineDate: string;
  priority: number;
  status: ProductionStatusEnum;
  outsourced: boolean;
  tasks: number[];
  type: string;
  orderCompanyName: string;
  orderDate: string;
};

export type ProductionListType = {
  items: ProductionVMType[];
  page: number;
  pageSize: number;
  total: number;
};

export type ProductionTaskListType = {
  items: ProductionTaskDetailedType[];
  page: number;
  pageSize: number;
  total: number;
};

export type ProductionsSummaryType = {
  reviewReady: number;
  camReady: number;
  productionReady: number;
  afterworkReady: number;
  shippingReady: number;
  customReady: number;
  totalActive: number;
  machineInfo: {
    [key: string]: {
      items: number;
      label: string;
      time: number;
    };
  };
};
